import { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { captureAttributionFromUrl, trackEvent } from './utils/attribution';
import { Toaster } from './components/ui/sonner';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './i18n'; // Initialize i18n
import i18n from './i18n';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import ApkUpdaterBanner from './components/ApkUpdaterBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { storeToken } from './utils/tokenManager';
import PageTransition from './components/PageTransition';
import { ensureHealthyBase } from './utils/backendUrl';

// Pre-probe the backend at app startup so the LoginPage health check (and
// every subsequent /api/* call) hits a host that's actually serving the
// FlavorFeed backend. Without this, custom domains like flavorfeed.ai try
// the hardcoded `api.flavorfeed.ai` (not configured) → "Login service
// unavailable" banner. The probe runs in parallel with module loading so
// it almost always completes before the user clicks "Sign in".
if (typeof window !== 'undefined' && !window.__ff_base_probe_started__) {
  window.__ff_base_probe_started__ = true;
  ensureHealthyBase().catch(() => { /* probe never throws, but be safe */ });
}

// Global safety net for stale lazy-loaded chunks after a deploy.
// React's `lazy(() => import(...))` calls reject with ChunkLoadError if the
// hashed chunk filename no longer exists on the CDN (typical right after we
// ship new frontend code). Without this handler the user sees a scary error
// page; with it we force one hard reload to fetch the fresh bundle.
if (typeof window !== 'undefined' && !window.__ff_chunk_handler_installed__) {
  window.__ff_chunk_handler_installed__ = true;
  const tryReloadOnStaleChunk = (err) => {
    try {
      const msg =
        (err && (err.message || err.reason?.message || (typeof err === 'string' ? err : ''))) || '';
      const name = err?.name || err?.reason?.name || '';
      const isChunkErr =
        name === 'ChunkLoadError' ||
        /Loading chunk \d+ failed/i.test(msg) ||
        /Loading CSS chunk/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg);
      if (!isChunkErr) return;
      const flag = 'ff_chunk_reload_at';
      const last = parseInt(sessionStorage.getItem(flag) || '0', 10);
      const now = Date.now();
      if (!last || now - last > 60_000) {
        sessionStorage.setItem(flag, String(now));
        window.location.reload(true);
      }
    } catch { /* never crash */ }
  };
  window.addEventListener('error', tryReloadOnStaleChunk);
  window.addEventListener('unhandledrejection', tryReloadOnStaleChunk);
}

// Eager load critical pages
import Landing from './pages/Landing';

// SEO marketing pages — EAGERLY imported (no lazy/Suspense) so
// react-snap can capture their Helmet <title>/meta/JSON-LD at
// snapshot time. With lazy() the Suspense fallback would race the
// snapshot, leaving generic titles on /vs/*, /regions/*, /blog/*.
// Bundle impact is ~25 KB (acceptable — these are tiny marketing
// pages with no heavy deps).
import ComparisonPage from './pages/comparisons/ComparisonPage';
import RegionLandingPage from './pages/regions/RegionLandingPage';
import ArticlePage, { BlogIndex as BlogIndexLoader } from './pages/blog/ArticlePage';
import SavingsCalculator from './pages/SavingsCalculator';

// Lazy load non-critical pages
const UnifiedLogin = lazy(() => import('./pages/UnifiedLogin'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const POSSignup = lazy(() => import('./pages/POSSignup'));
const POSSystem = lazy(() => import('./pages/POSSystem'));
const POSDownloads = lazy(() => import('./pages/POSDownloads'));
const KitchenDisplay = lazy(() => import('./pages/KitchenDisplay'));
const CustomerDisplay = lazy(() => import('./components/CustomerDisplay'));
const Reports = lazy(() => import('./pages/Reports'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const LogoPreview = lazy(() => import('./pages/LogoPreview'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// NEW: Separated POS Terminal and Admin Dashboard
const POSTerminal = lazy(() => import('./pages/POSTerminal'));
const POSAdminDashboard = lazy(() => import('./pages/POSAdminDashboard'));
const Help = lazy(() => import('./pages/Help'));
const HelpSimple = lazy(() => import('./pages/HelpSimple'));
const Support = lazy(() => import('./pages/Support'));
const POSSupportPage = lazy(() => import('./pages/SupportPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const SchedulingGuide = lazy(() => import('./pages/SchedulingGuide'));
const DigitalMenu = lazy(() => import('./pages/DigitalMenu'));
const PublicReservation = lazy(() => import('./pages/PublicReservation'));
const DigitalSignage = lazy(() => import('./pages/DigitalSignage'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancelled = lazy(() => import('./pages/PaymentCancelled'));

// Loyalty Portal Pages
const LoyaltyPortal = lazy(() => import('./pages/LoyaltyPortal'));
const LoyaltyRegister = lazy(() => import('./pages/LoyaltyRegister'));
const LoyaltyLogin = lazy(() => import('./pages/LoyaltyLogin'));
const LoyaltyDashboard = lazy(() => import('./pages/LoyaltyDashboard'));
const LoyaltyRestaurants = lazy(() => import('./pages/LoyaltyRestaurants'));
const LoyaltyOffers = lazy(() => import('./pages/LoyaltyOffers'));
const LoyaltyQRGenerator = lazy(() => import('./pages/LoyaltyQRGenerator'));

// AI Demo Page
const AIDemo = lazy(() => import('./pages/AIDemo'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TVSetup = lazy(() => import('./pages/TVSetup'));

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
    <div className="w-10 h-10 border-2 border-[#27272A] border-t-[#8B5CF6] rounded-full animate-spin"></div>
  </div>
);

import getBackendUrl from './utils/backendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Initialize language immediately from localStorage (before React renders)
const initializeLanguage = () => {
  const savedLang = localStorage.getItem('language') || 'en';
  i18n.changeLanguage(savedLang);
  document.documentElement.lang = savedLang;
  document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
  document.body.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
};

// Call immediately
initializeLanguage();

// Load language from settings on app startup (async)
const loadLanguageSettings = async () => {
  try {
    // Only fetch backend settings if user is authenticated
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      const response = await axios.get(`${BACKEND_URL}/api/pos/settings`, { 
        timeout: 3000,
        withCredentials: true 
      });
      const language = response.data.language || 'en';
      if (language !== i18n.language) {
        i18n.changeLanguage(language);
        document.documentElement.lang = language;
        document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
        document.body.dir = language === 'ar' ? 'rtl' : 'ltr';
        localStorage.setItem('language', language);
      }
    }
  } catch (error) {
    // Silently fail - we already initialized from localStorage
    console.log('Backend language settings not available, using localStorage');
  }
};

// Don't auto-load backend settings on app startup
// This will be handled by individual components if needed
// setTimeout(loadLanguageSettings, 100);

// Check if running as PWA (standalone mode)
const isPWAMode = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

// PWA Redirect Handler - ALWAYS redirects to POS Terminal when in PWA mode
const PWARedirectHandler = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // In PWA mode, always prefer POS Terminal
    if (isPWAMode()) {
      const posToken = localStorage.getItem('pos_token');
      
      // If on home page or admin dashboard, redirect to POS terminal
      if (location.pathname === '/' || location.pathname === '' || location.pathname === '/admin-dashboard') {
        if (posToken) {
          navigate('/pos-terminal', { replace: true });
        } else {
          // Not logged in - go to unified login
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        }
      }
      
      // If on pos-admin (legacy) and already logged in, go to terminal
      if ((location.pathname === '/pos-admin') && posToken) {
        navigate('/pos-terminal', { replace: true });
      }
    }
  }, [location.pathname, navigate]);
  
  return children;
};

// Auth wrapper component
function AuthHandler({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Jun 2026 — first-touch attribution. Captures `?ref=` and `?utm_*`
  // params once, stores in localStorage (30-day TTL), and fires a
  // GA4 `landing_view` event so we can measure which marketing video
  // / channel actually brings sign-ups. No-ops in dev when env var
  // REACT_APP_GA4_ID isn't set.
  useEffect(() => {
    const captured = captureAttributionFromUrl();
    if (captured) {
      trackEvent('landing_view', { ref: captured.ref, utm_source: captured.utm_source });
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔍 CheckAuth started');
      console.log('Current pathname:', location.pathname);
      console.log('Current hash:', window.location.hash);
      
      // Check for session_id in URL fragment (after OAuth redirect)
      const hash = window.location.hash;
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        console.log('Found session_id in URL:', sessionId);
        
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          setUser(response.data.user);
          
          // Clean URL
          window.history.replaceState(null, '', '/');
          
          // Check if this email also exists as a POS user — if so, redirect to POS dashboard
          try {
            const email = response.data.user?.email;
            if (email) {
              const posCheck = await axios.get(`${API}/pos/auth/check-email?email=${encodeURIComponent(email)}`);
              if (posCheck.data?.is_pos_user && posCheck.data?.pos_token) {
                // Store POS credentials and redirect to POS dashboard
                storeToken(posCheck.data.pos_token, 30);
                localStorage.setItem('pos_user', JSON.stringify(posCheck.data.user));
                localStorage.setItem('restaurant_id', posCheck.data.user.restaurant_id);
                await new Promise(resolve => setTimeout(resolve, 100));
                navigate('/admin-dashboard', { replace: true });
                setLoading(false);
                return;
              }
            }
          } catch {
            // Not a POS user — continue to platform dashboard
          }
          
          await new Promise(resolve => setTimeout(resolve, 100));
          navigate('/dashboard', { replace: true });
          setLoading(false);
        } catch (error) {
          console.error('❌ Auth error:', error);
          console.error('❌ Error response:', error.response?.data);
          setUser(null);
          setLoading(false);
          navigate('/', { replace: true });
        }
        return;
      }

      // Check existing session only if not already authenticated
      if (!user) {
        try {
          console.log('🔐 Checking existing session with /auth/me...');
          const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
          console.log('✅ Auth me response:', response.data);
          setUser(response.data);
          
          // If authenticated and on landing page, redirect to dashboard
          // BUT check for checkout parameters first
          const urlParams = new URLSearchParams(window.location.search);
          const hasCheckoutParams = urlParams.get('checkout') && urlParams.get('billing');
          
          if ((location.pathname === '/' || location.pathname === '') && !hasCheckoutParams) {
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.log('⚠️ Not authenticated');
          setUser(null);
          // If trying to access dashboard without auth, redirect to landing
          if (location.pathname === '/dashboard') {
            navigate('/', { replace: true });
          }
        } finally {
          setLoading(false);
        }
      } else {
        // User already set, just stop loading
        setLoading(false);
      }
    };

    checkAuth();
  }, []); // eslint-disable-line

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-500"></div>
      </div>
    );
  }

  return children({ user, setUser });
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <CurrencyProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <PageTransition>
                <Routes>
              <Route path="/" element={
                <AuthHandler>
                  {({ user, setUser }) => {
                    // Check if running as PWA - redirect to POS
                    if (isPWAMode()) {
                      const posToken = localStorage.getItem('pos_token');
                      return posToken ? <Navigate to="/pos-terminal" /> : <Navigate to="/login" />;
                    }
                    
                    // Check for checkout parameters before redirecting authenticated users
                    const urlParams = new URLSearchParams(window.location.search);
                    const hasCheckoutParams = urlParams.get('checkout') && urlParams.get('billing');
                    
                    // If user is authenticated but has checkout params, show Landing to process checkout
                    if (user && hasCheckoutParams) {
                      return <Landing user={user} />;
                    }
                    
                    // Normal routing: authenticated users go to dashboard, others see landing
                    return user ? <Navigate to="/dashboard" /> : <Landing />;
                  }}
                </AuthHandler>
              } />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/pos" element={<Navigate to="/login?tab=quick" replace />} />
              <Route path="/signup" element={<POSSignup />} />
              {/* Legacy: keep UnifiedLogin available so old bookmarks/tests still work */}
              <Route path="/login-classic" element={<UnifiedLogin />} />
              <Route path="/dashboard" element={
                <AuthHandler>
                  {({ user, setUser }) => 
                    user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/" />
                  }
                </AuthHandler>
              } />
              <Route path="/portfolio" element={
                <AuthHandler>
                  {({ user, setUser }) => 
                    user ? <Portfolio user={user} /> : <Navigate to="/" />
                  }
                </AuthHandler>
              } />
              <Route path="/help" element={<Help />} />
              <Route path="/scheduling-guide" element={<SchedulingGuide />} />
              <Route path="/support" element={
                <AuthHandler>
                  {({ user, setUser }) => <Support user={user} />}
                </AuthHandler>
              } />
              <Route path="/admin" element={
                <AuthHandler>
                  {({ user, setUser }) => <AdminDashboard user={user} />}
                </AuthHandler>
              } />
              <Route path="/logos" element={<LogoPreview />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/menu" element={<DigitalMenu />} />
              <Route path="/menu/:restaurantId" element={<DigitalMenu />} />
              <Route path="/book/:restaurantCode" element={<PublicReservation />} />
              <Route path="/signage/:restaurantId" element={<DigitalSignage />} />
              <Route path="/tv" element={<TVSetup />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancelled" element={<PaymentCancelled />} />
              <Route path="/customer" element={<CustomerDashboard />} />
              <Route path="/pos-downloads" element={<POSDownloads />} />
              
              {/* AI Demo Page */}
              <Route path="/demo" element={<AIDemo />} />
              
              {/* Loyalty Portal Routes */}
              <Route path="/loyalty" element={<LoyaltyPortal />} />
              <Route path="/loyalty/register" element={<LoyaltyRegister />} />
              <Route path="/loyalty/login" element={<LoyaltyLogin />} />
              <Route path="/loyalty/dashboard" element={<LoyaltyDashboard />} />
              <Route path="/loyalty/restaurants" element={<LoyaltyRestaurants />} />
              <Route path="/loyalty/offers" element={<LoyaltyOffers />} />
              <Route path="/loyalty/qr-generator" element={<LoyaltyQRGenerator />} />
              
              {/* POS Routes - Unified login redirects */}
              <Route path="/pos-login" element={<Navigate to="/login" replace />} />
              <Route path="/pos-admin" element={<Navigate to="/login" replace />} />
              <Route path="/pos-reset-password" element={<ResetPassword />} />
              <Route path="/pos-signup" element={<POSSignup />} />
              <Route path="/pos-system" element={<ErrorBoundary><POSSystem /></ErrorBoundary>} />
              <Route path="/kitchen" element={<ErrorBoundary><KitchenDisplay /></ErrorBoundary>} />
              <Route path="/customer-display" element={<ErrorBoundary><CustomerDisplay /></ErrorBoundary>} />
              <Route path="/customer-display/:restaurantCode" element={<ErrorBoundary><CustomerDisplay /></ErrorBoundary>} />
              <Route path="/customer-display/:restaurantCode/:branchCode" element={<ErrorBoundary><CustomerDisplay /></ErrorBoundary>} />
              <Route path="/reports" element={<ErrorBoundary><Reports /></ErrorBoundary>} />
              <Route path="/pos" element={<Navigate to="/login" replace />} />
              <Route path="/pos-support" element={<ErrorBoundary><POSSupportPage /></ErrorBoundary>} />
              
              {/* NEW: Separated POS Terminal and Admin Dashboard */}
              <Route path="/pos-terminal" element={<ErrorBoundary><POSTerminal /></ErrorBoundary>} />
              <Route path="/admin-dashboard" element={<ErrorBoundary><POSAdminDashboard /></ErrorBoundary>} />

              {/* SEO: Competitor comparison pages (ranks for "{competitor} alternative") */}
              <Route path="/vs/:competitor" element={<ErrorBoundary><ComparisonPage /></ErrorBoundary>} />

              {/* SEO: Region/country landing pages (ranks for "restaurant POS {country}") */}
              <Route path="/regions/:slug" element={<ErrorBoundary><RegionLandingPage /></ErrorBoundary>} />

              {/* SEO: Blog */}
              <Route path="/blog" element={<ErrorBoundary><BlogIndexLoader /></ErrorBoundary>} />
              <Route path="/blog/:slug" element={<ErrorBoundary><ArticlePage /></ErrorBoundary>} />

              {/* Interactive savings calculator (backlink magnet) */}
              <Route path="/calculator" element={<ErrorBoundary><SavingsCalculator /></ErrorBoundary>} />
              </Routes>
              </PageTransition>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" />
          <PWAInstallPrompt />
          <OfflineIndicator />
          <ApkUpdaterBanner />
        </div>
      </CurrencyProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;